<template>
  <div>
    <Navbar activeDirectory="admin" activePage="admin-companies"></Navbar>
    <div class="container-fluid py-4">
      <div class="row">
        <div class="col-12">

          <!-- Page Header -->
          <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap text-left">
            <div class="d-flex align-items-center">
              <div class="cw-header-icon mr-3">
                <i class="mdi mdi-domain"></i>
              </div>
              <div class="text-left">
                <h3 class="mb-0 font-weight-bold text-dark">Gestione Aziende</h3>
                <p class="mb-0 text-muted small">Crea e configura i tenant (aziende)</p>
              </div>
            </div>
            <div class="d-flex mt-2 mt-md-0" style="gap: 8px;" v-if="mode === 'list'">
              <button
                class="cw-btn-submit text-decoration-none"
                @click="setCreateMode"
              >
                <i class="mdi mdi-plus mr-1"></i> Nuova Azienda
              </button>
            </div>
          </div>

          <!-- LIST MODE -->
          <div v-if="mode === 'list'">
            <div class="row mb-4">
              <!-- Stats -->
              <div class="col-md-3 col-sm-6 mb-3" v-for="stat in stats" :key="stat.label">
                <div class="card border-0 shadow-sm rounded-lg h-100">
                  <div class="card-body d-flex align-items-center p-3">
                    <div class="stat-icon mr-3" :style="{ background: stat.color }">
                      <i :class="stat.icon"></i>
                    </div>
                    <div>
                      <div class="stat-number font-weight-bold">{{ stat.value }}</div>
                      <div class="stat-label small text-muted">{{ stat.label }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Companies Table -->
            <div class="cw-card">
              <div class="row mb-3">
                  <div class="col-md-4 col-sm-6">
                    <div class="d-flex align-items-center shadow-sm rounded-pill bg-light border px-3 search-box-capsule" style="height: 38px;">
                      <i class="ti-search text-muted mr-2"></i>
                      <input
                        v-model="searchQuery"
                        class="border-0 bg-transparent p-0 flex-grow-1"
                        placeholder="Cerca azienda..."
                        style="box-shadow: none; outline: none; height: 100%; width: 100%; color: var(--text-main); font-size: 14px;"
                      >
                    </div>
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="au-table">
                    <thead>
                      <tr>
                        <th style="width: 60px;">ID</th>
                        <th>Nome Azienda</th>
                        <th>Partita IVA</th>
                        <th>Contatti</th>
                        <th class="text-right" style="width: 140px;">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="loadingCompanies">
                        <td colspan="5" class="text-center py-4">
                          <span class="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                          Caricamento aziende...
                        </td>
                      </tr>
                      <tr v-else-if="filteredCompanies.length === 0">
                        <td colspan="5" class="text-center py-4 text-muted">
                          <i class="ti-building d-block mb-2" style="font-size: 2rem; opacity: 0.3;"></i>
                          Nessuna azienda registrata.
                        </td>
                      </tr>
                      <tr v-for="company in filteredCompanies" :key="company.id" v-else class="company-row">
                        <td class="font-weight-bold text-muted">{{ company.id }}</td>
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="company-avatar mr-2">{{ company.nome.charAt(0).toUpperCase() }}</div>
                            <span class="font-weight-semibold text-dark">{{ company.nome }}</span>
                          </div>
                        </td>
                        <td>
                          <code class="text-secondary">{{ company.partita_iva || '—' }}</code>
                        </td>
                        <td>
                          <div v-if="company.email || company.telefono" class="small">
                            <div v-if="company.email"><i class="mdi mdi-email mr-1"></i>{{ company.email }}</div>
                            <div v-if="company.telefono"><i class="mdi mdi-phone mr-1"></i>{{ company.telefono }}</div>
                          </div>
                          <span v-else class="text-muted small italic">—</span>
                        </td>
                        <td class="text-right text-nowrap">
                          <button
                            class="btn btn-sm btn-outline-info rounded-pill px-3 mr-1 font-weight-semibold"
                            @click="openEditCommesse(company)"
                            title="Gestisci Commesse"
                          >
                            <i class="ti-briefcase mr-1"></i>Commesse
                          </button>
                          <button
                            class="btn btn-sm btn-outline-primary rounded-pill px-3 mr-1 font-weight-semibold"
                            @click="editCompany(company)"
                            title="Modifica Azienda"
                          >
                            <i class="mdi mdi-pencil"></i>
                          </button>
                          <button
                            class="au-btn-danger"
                            @click="deleteCompany(company.id)"
                            title="Elimina Azienda"
                          >
                            <i class="mdi mdi-trash-can-outline"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
            </div>
          </div>

          <!-- CREATE / EDIT COMPANY MODE -->
          <div v-if="mode === 'create' || mode === 'edit'" class="row justify-content-center">
            <div class="col-lg-6 col-md-8 mb-4">
              <div class="cw-card text-left">
                  <div class="cw-section-title">{{ mode === 'edit' ? 'Modifica Azienda' : 'Nuova Azienda' }}</div>

                  <form @submit.prevent="saveCompany">
                    <div class="form-group mb-3 text-left">
                      <label class="cw-label">Nome Azienda *</label>
                      <div class="cw-input-wrap">
                        <i class="mdi mdi-domain cw-field-icon"></i>
                        <input
                          v-model="form.nome"
                          class="cw-input"
                          placeholder="Es. Comune di Roma"
                          required
                        >
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-md-6 form-group mb-3 text-left">
                        <label class="cw-label">Partita IVA</label>
                        <div class="cw-input-wrap">
                          <i class="mdi mdi-identifier cw-field-icon"></i>
                          <input v-model="form.partita_iva" class="cw-input" placeholder="Es. 01234567890">
                        </div>
                      </div>
                      <div class="col-md-6 form-group mb-3 text-left">
                        <label class="cw-label">Telefono</label>
                        <div class="cw-input-wrap">
                          <i class="mdi mdi-phone cw-field-icon"></i>
                          <input v-model="form.telefono" class="cw-input" placeholder="Es. +39 06 1234567">
                        </div>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-md-6 form-group mb-3 text-left">
                        <label class="cw-label">Email Principale</label>
                        <div class="cw-input-wrap">
                          <i class="mdi mdi-email cw-field-icon"></i>
                          <input v-model="form.email" type="email" class="cw-input" placeholder="info@azienda.it">
                        </div>
                      </div>
                      <div class="col-md-6 form-group mb-3 text-left">
                        <label class="cw-label">Indirizzo PEC</label>
                        <div class="cw-input-wrap">
                          <i class="mdi mdi-email-secure cw-field-icon"></i>
                          <input v-model="form.pec" type="email" class="cw-input" placeholder="azienda@pec.it">
                        </div>
                      </div>
                    </div>

                    <h6 class="mt-3 mb-3 text-left font-weight-bold text-muted border-bottom pb-2">Sede Legale / Operativa</h6>
                    <div class="form-group mb-3 text-left">
                      <label class="cw-label">Indirizzo Completo</label>
                      <div class="cw-input-wrap">
                        <i class="mdi mdi-map-marker cw-field-icon"></i>
                        <input v-model="form.indirizzo" class="cw-input" placeholder="Via Roma, 10">
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-5 form-group mb-3 text-left">
                        <label class="cw-label">Città</label>
                        <div class="cw-input-wrap">
                          <input v-model="form.citta" class="cw-input" placeholder="Roma">
                        </div>
                      </div>
                      <div class="col-md-4 form-group mb-3 text-left">
                        <label class="cw-label">Provincia</label>
                        <div class="cw-input-wrap">
                          <input v-model="form.provincia" class="cw-input" placeholder="RM" maxlength="2">
                        </div>
                      </div>
                      <div class="col-md-3 form-group mb-3 text-left">
                        <label class="cw-label">CAP</label>
                        <div class="cw-input-wrap">
                          <input v-model="form.cap" class="cw-input" placeholder="00100">
                        </div>
                      </div>
                    </div>

                    <h6 class="mt-3 mb-3 text-left font-weight-bold text-muted border-bottom pb-2">Referente Principale</h6>
                    <div class="row">
                      <div class="col-md-6 form-group mb-3 text-left">
                        <label class="cw-label">Nome Referente</label>
                        <div class="cw-input-wrap">
                          <i class="mdi mdi-account cw-field-icon"></i>
                          <input v-model="form.nome_referente" class="cw-input" placeholder="Mario">
                        </div>
                      </div>
                      <div class="col-md-6 form-group mb-4 text-left">
                        <label class="cw-label">Cognome Referente</label>
                        <div class="cw-input-wrap">
                          <input v-model="form.cognome_referente" class="cw-input" placeholder="Rossi">
                        </div>
                      </div>
                    </div>

                    <div class="cw-footer mt-4 d-flex justify-content-end align-items-center" style="gap: 8px;">
                      <button
                        type="button"
                        class="btn btn-light rounded-pill px-4 font-weight-semibold border"
                        @click="mode = 'list'"
                      >
                        Annulla
                      </button>
                      <button
                        type="submit"
                        class="cw-btn-submit"
                        :disabled="submitting"
                      >
                        <span class="spinner-border spinner-border-sm mr-1" v-if="submitting"></span>
                        <i class="mdi mdi-check-circle-outline mr-1" v-else></i> {{ mode === 'edit' ? 'Salva Modifiche' : 'Crea Azienda' }}
                      </button>
                    </div>
                  </form>
              </div>
            </div>
          </div>

          <!-- EDIT MODULES MODE -->
          <div v-if="mode === 'edit-commessa-modules'" class="row justify-content-center">
            <div class="col-lg-9 col-md-11 mb-4">
              <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:12px;">
                <h4 class="mb-0 font-weight-bold text-dark">
                  Moduli di <span class="text-primary">{{ selectedCommessa && selectedCommessa.nome }}</span>
                </h4>
                <div class="d-flex" style="gap:8px;">
                  <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="setCreateModuleMode">
                    <i class="mdi mdi-plus mr-1"></i> Nuovo Modulo
                  </button>
                  <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'edit-commesse'">
                    <i class="mdi mdi-arrow-left mr-1"></i> Torna a Commesse
                  </button>
                </div>
              </div>

              <div v-if="loadingModules" class="text-center py-5">
                <span class="spinner-border text-primary" role="status"></span>
                <p class="text-muted mt-2">Caricamento moduli...</p>
              </div>

              <div v-else>
                <!-- Griglia moduli -->
                <div class="cw-card mb-4">
                  <div class="cw-section-title">
                    <span class="cw-step-num">1</span>Moduli della Commessa
                  </div>
                  <p class="text-muted small mb-3">Seleziona un modulo per configurare i suoi asset e interventi.</p>
                  <div class="modules-grid">
                    <div
                      v-for="mod in commessaModules"
                      :key="mod.id"
                      class="module-card"
                      :class="{ 'module-card--active': selectedConfigModule && selectedConfigModule.id === mod.id }"
                      @click="selectConfigModule(mod)"
                    >
                      <i :class="mod.icona || 'mdi mdi-layers'" class="module-card__icon"></i>
                      <span class="module-card__name">{{ mod.nome }}</span>
                      <span class="module-card__check" v-if="selectedConfigModule && selectedConfigModule.id === mod.id"><i class="mdi mdi-check"></i></span>
                    </div>
                    <div v-if="commessaModules.length === 0" class="text-muted small py-3" style="grid-column: 1 / -1; text-align:center;">
                      Nessun modulo ancora creato per questa commessa.
                    </div>
                  </div>
                </div>

                <!-- Configurazione Asset e Interventi del Modulo Selezionato -->
                <div v-if="selectedConfigModule" class="row mb-4">
                  <!-- Colonna sinistra: Asset del modulo -->
                  <div class="col-md-6 mb-4 mb-md-0">
                    <div class="cw-card h-100">
                      <div class="cw-section-title text-left">
                        <span class="cw-step-num">2</span>Asset di {{ selectedConfigModule.nome }}
                      </div>
                      
                      <div v-if="loadingAssets" class="text-center py-4">
                        <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                        <p class="text-muted mt-1 small">Caricamento asset...</p>
                      </div>
                      
                      <div v-else class="table-responsive mb-4" style="max-height: 250px; overflow-y: auto;">
                        <table class="au-table">
                          <thead>
                            <tr>
                              <th>Nome</th>
                              <th>Codice</th>
                              <th class="text-right">Azioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-if="configModuleAssets.length === 0">
                              <td colspan="3" class="text-muted text-center small py-3">
                                Nessun asset configurato per questo modulo.
                              </td>
                            </tr>
                            <tr
                              v-else
                              v-for="asset in configModuleAssets"
                              :key="asset.id"
                              @click="selectConfigAssetType(asset)"
                              style="cursor: pointer;"
                              :class="{ 'table-active font-weight-bold text-primary': selectedConfigAssetType && selectedConfigAssetType.id === asset.id }"
                            >
                              <td class="text-left">{{ asset.nome }}</td>
                              <td class="text-left"><code class="text-primary">{{ asset.codice }}</code></td>
                              <td class="text-right text-nowrap" @click.stop>
                                <button
                                  class="btn btn-sm btn-outline-danger rounded-pill px-2 py-1"
                                  style="font-size:0.75rem;"
                                  @click="deleteConfigAsset(asset.id)"
                                  title="Elimina Asset"
                                >
                                  <i class="mdi mdi-trash-can-outline mr-1"></i>Elimina
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Form per aggiungere un asset type nel catalogo completo -->
                      <div class="border-top pt-3 text-left">
                        <h6 class="font-weight-bold text-dark mb-2">Aggiungi Asset</h6>
                        <p class="text-muted small mb-3">Crea nuove tipologie di asset nel catalogo definendo i loro campi dinamici.</p>
                        <router-link
                          v-if="selectedConfigModule"
                          :to="{ name: 'admin-assets', query: { action: 'add', fk_module: selectedConfigModule.id } }"
                          class="cw-btn-submit w-100 text-center text-white"
                          style="text-decoration: none;"
                        >
                          <i class="mdi mdi-plus-circle-outline mr-1"></i> Configura nel Catalogo
                        </router-link>
                      </div>
                    </div>
                  </div>

                  <!-- Colonna destra: Tipologie Intervento dell'asset -->
                  <div class="col-md-6">
                    <div class="cw-card h-100">
                      <div class="cw-section-title text-left">
                        <span class="cw-step-num">3</span>Tipologie Intervento
                      </div>

                      <div v-if="!selectedConfigAssetType" class="text-center py-5 text-muted">
                        <i class="mdi mdi-gesture-tap text-primary" style="font-size: 2.5rem; display:block; opacity:0.6;"></i>
                        Seleziona un asset a sinistra per gestirne le tipologie di intervento.
                      </div>

                      <div v-else>
                        <h6 class="font-weight-bold text-dark mb-2 text-left">
                          Interventi per <span class="text-primary">{{ selectedConfigAssetType.nome }}</span>
                        </h6>
                        <p class="text-muted small mb-3 text-left">Associa le tipologie di intervento applicabili a questo asset:</p>

                        <!-- Griglia/Chips delle associazioni -->
                        <div class="mb-4 text-left" style="max-height: 200px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px;">
                          <div v-if="loadingInterventions" class="text-center py-3">
                            <span class="spinner-border spinner-border-sm text-primary"></span>
                          </div>
                          <div v-else-if="filteredInterventions.length === 0" class="text-muted text-center small py-2">
                            Nessuna tipologia creata per questo modulo.
                          </div>
                          <div v-else class="d-flex flex-wrap" style="gap: 8px;">
                            <label
                              v-for="inv in filteredInterventions"
                              :key="inv.id"
                              class="cw-chip"
                              :class="{ 'cw-chip-active': selectedConfigAssetType.interventi_ids.includes(inv.id) }"
                              style="cursor: pointer; margin: 0; user-select: none;"
                            >
                              <input
                                type="checkbox"
                                :value="inv.id"
                                :checked="selectedConfigAssetType.interventi_ids.includes(inv.id)"
                                @change="toggleInterventoAssociation(inv.id)"
                                style="display: none;"
                              >
                              {{ inv.nome }}
                            </label>
                          </div>
                        </div>

                        <!-- Form per creare e associare un intervento al volo -->
                        <div class="border-top pt-3 text-left">
                          <h6 class="font-weight-bold text-dark mb-3">Crea Nuovo Intervento</h6>
                          <form @submit.prevent="createNewInterventoForAsset">
                            <div class="row align-items-end">
                              <div class="col-sm-6 mb-2 mb-sm-0 text-left">
                                <label class="cw-label">Nome Intervento <span class="text-danger">*</span></label>
                                <div class="cw-input-wrap">
                                  <i class="mdi mdi-wrench cw-field-icon"></i>
                                  <input
                                    v-model="newInterventoForm.nome"
                                    class="cw-input"
                                    placeholder="Es. Sostituzione Lampada"
                                    required
                                    @input="generateNewInterventoCodice"
                                  >
                                </div>
                              </div>
                              <div class="col-sm-6 text-left">
                                <label class="cw-label">Codice (Autogenerato) <span class="text-danger">*</span></label>
                                <div class="cw-input-wrap">
                                  <i class="mdi mdi-code-tags cw-field-icon"></i>
                                  <input
                                    v-model="newInterventoForm.codice"
                                    class="cw-input"
                                    style="background:#f8fafc;"
                                    required
                                    readonly
                                  >
                                </div>
                              </div>
                            </div>
                            <button type="submit" class="cw-btn-submit w-100 mt-3" :disabled="submittingIntervento">
                              <span class="spinner-border spinner-border-sm mr-2" v-if="submittingIntervento"></span>
                              <i class="mdi mdi-plus-circle-outline mr-1" v-else></i>
                              Crea e Associa
                            </button>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tipologie Squadra Section -->
                <div v-if="selectedConfigModule" class="row mb-4">
                  <div class="col-md-12">
                    <div class="cw-card">
                      <div class="cw-section-title text-left">
                        <span class="cw-step-num">4</span>Tipologie Squadra
                      </div>
                      
                      <div class="d-flex justify-content-between align-items-start mb-3 text-left">
                        <div>
                          <h6 class="font-weight-bold text-dark mb-1">Squadre per {{ selectedConfigModule.nome }}</h6>
                          <p class="text-muted small mb-0">
                            Di seguito le tipologie di squadra attive e associate a questo modulo.
                          </p>
                        </div>
                        <router-link
                          :to="{ name: 'admin-team-types' }"
                          class="cw-btn-submit"
                          style="text-decoration: none;"
                        >
                          <i class="mdi mdi-account-group-outline mr-1"></i> Configura
                        </router-link>
                      </div>

                      <div v-if="loadingTeamTypes" class="text-center py-3">
                        <span class="spinner-border spinner-border-sm text-primary"></span>
                      </div>
                      <div v-else-if="configModuleTeamTypes.length === 0" class="text-muted text-center small py-3" style="border: 1px dashed #e2e8f0; border-radius: 8px;">
                        Nessuna tipologia di squadra associata a questo modulo.
                      </div>
                      <div v-else class="d-flex flex-wrap text-left" style="gap: 8px;">
                        <span
                          v-for="team in configModuleTeamTypes"
                          :key="team.id"
                          class="cw-chip cw-chip-active"
                          style="cursor: default;"
                        >
                          <i class="mdi mdi-account-group mr-1"></i> {{ team.nome }}
                          <span style="opacity: 0.8; font-size: 0.9em; margin-left: 4px;">({{ team.codice }})</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <!-- EDIT COMMESSE MODE -->
          <div v-if="mode === 'edit-commesse'" class="row justify-content-center">
            <div class="col-lg-12 col-md-12 mb-4">
              <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:12px;">
                <h4 class="mb-0 font-weight-bold text-dark">
                  Commesse di <span class="text-primary">{{ selectedCompany && selectedCompany.nome }}</span>
                </h4>
                <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'list'">
                  <i class="mdi mdi-arrow-left mr-1"></i> Torna all'elenco
                </button>
              </div>

              <div class="row">
                <!-- Lista Commesse -->
                <div class="col-md-7 mb-4">
                  <div class="cw-card h-100">
                    <div class="cw-section-title">
                      <span class="cw-step-num">1</span>Commesse Attive
                    </div>

                    <div v-if="loadingCommesse" class="text-center py-4">
                      <span class="spinner-border text-primary" role="status"></span>
                    </div>

                    <div v-else class="table-responsive" style="max-height: 420px; overflow-y: auto;">
                      <table class="au-table">
                        <thead>
                          <tr>
                            <th style="width:60px;">ID</th>
                            <th>Nome & Dettagli</th>
                            <th>CIG/CUP</th>
                            <th>Date</th>
                            <th class="text-right" style="width:140px;">Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="companyCommesse.length === 0">
                            <td colspan="3" class="text-muted text-center small py-4">
                              <i class="mdi mdi-briefcase-outline d-block mb-2" style="font-size:2rem; opacity:0.3;"></i>
                              Nessuna commessa registrata per questa azienda.
                            </td>
                          </tr>
                          <tr v-for="comm in companyCommesse" :key="comm.id" v-else>
                            <td class="font-weight-bold text-muted">{{ comm.id }}</td>
                            <td>
                              <div class="font-weight-semibold text-dark">{{ comm.nome }}</div>
                              <div class="small text-muted" v-if="comm.stazione_appaltante">{{ comm.stazione_appaltante }}</div>
                              <div class="small text-primary font-weight-bold" v-if="comm.importo">€ {{ comm.importo }}</div>
                            </td>
                            <td>
                              <div class="small"><span class="font-weight-bold" v-if="comm.cig">CIG:</span> {{ comm.cig || '-' }}</div>
                              <div class="small"><span class="font-weight-bold" v-if="comm.cup">CUP:</span> {{ comm.cup || '-' }}</div>
                            </td>
                            <td>
                              <div class="small"><i class="mdi mdi-calendar-start mr-1"></i> {{ comm.data_inizio || '-' }}</div>
                              <div class="small"><i class="mdi mdi-calendar-end mr-1"></i> {{ comm.data_fine || '-' }}</div>
                            </td>
                            <td class="text-right text-nowrap">
                              <button
                                class="btn btn-sm btn-outline-info rounded-pill px-3 mr-1 font-weight-semibold"
                                @click.prevent="openCommessaModules(comm)"
                                title="Moduli"
                              >
                                <i class="mdi mdi-layers-outline mr-1"></i>Moduli
                              </button>
                              <button
                                class="btn btn-sm btn-outline-primary rounded-pill px-3 mr-1 font-weight-semibold"
                                @click.prevent="editCommessa(comm)"
                                title="Modifica Commessa"
                              >
                                <i class="mdi mdi-pencil"></i>
                              </button>
                              <button
                                class="btn btn-sm btn-outline-danger rounded-pill px-3 font-weight-semibold"
                                @click="deleteCommessa(comm.id)"
                                title="Elimina Commessa"
                              >
                                <i class="mdi mdi-trash-can-outline mr-1"></i>Elimina
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- Nuova Commessa -->
                <div class="col-md-5">
                  <div class="cw-card h-100">
                    <div class="cw-section-title">
                      <span class="cw-step-num">2</span>{{ commessaForm.id ? 'Modifica Commessa' : 'Aggiungi Commessa' }}
                    </div>
                    <form @submit.prevent="saveCommessa">
                      <div class="form-group mb-3 text-left">
                        <label class="cw-label">Nome Commessa <span class="text-danger">*</span></label>
                        <div class="cw-input-wrap">
                          <i class="mdi mdi-briefcase-outline cw-field-icon"></i>
                          <input v-model="commessaForm.nome" class="cw-input" placeholder="Es. Lavori Roma 2026" required>
                        </div>
                      </div>
                      
                      <div class="row">
                        <div class="col-md-6 form-group mb-3 text-left">
                          <label class="cw-label">Codice Commessa</label>
                          <div class="cw-input-wrap">
                            <input v-model="commessaForm.codice_commessa" class="cw-input" placeholder="Es. C-2026">
                          </div>
                        </div>
                        <div class="col-md-6 form-group mb-3 text-left">
                          <label class="cw-label">Importo (€)</label>
                          <div class="cw-input-wrap">
                            <i class="mdi mdi-currency-eur cw-field-icon"></i>
                            <input v-model="commessaForm.importo" type="number" step="0.01" class="cw-input" placeholder="0.00">
                          </div>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-md-6 form-group mb-3 text-left">
                          <label class="cw-label">CIG</label>
                          <div class="cw-input-wrap">
                            <input v-model="commessaForm.cig" class="cw-input" placeholder="Codice CIG">
                          </div>
                        </div>
                        <div class="col-md-6 form-group mb-3 text-left">
                          <label class="cw-label">CUP</label>
                          <div class="cw-input-wrap">
                            <input v-model="commessaForm.cup" class="cw-input" placeholder="Codice CUP">
                          </div>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-md-6 form-group mb-3 text-left">
                          <label class="cw-label">Data Inizio</label>
                          <div class="cw-input-wrap">
                            <input v-model="commessaForm.data_inizio" type="date" class="cw-input">
                          </div>
                        </div>
                        <div class="col-md-6 form-group mb-3 text-left">
                          <label class="cw-label">Data Fine</label>
                          <div class="cw-input-wrap">
                            <input v-model="commessaForm.data_fine" type="date" class="cw-input">
                          </div>
                        </div>
                      </div>

                      <div class="form-group mb-3 text-left">
                        <label class="cw-label">Stazione Appaltante</label>
                        <div class="cw-input-wrap">
                          <i class="mdi mdi-domain cw-field-icon"></i>
                          <input v-model="commessaForm.stazione_appaltante" class="cw-input" placeholder="Es. Comune di Milano">
                        </div>
                      </div>

                      <div class="form-group mb-4 text-left">
                        <label class="cw-label">Descrizione</label>
                        <textarea v-model="commessaForm.descrizione" class="cw-input" rows="2" style="border-radius: 12px; padding: 12px;" placeholder="Dettagli aggiuntivi sulla commessa..."></textarea>
                      </div>

                      <div class="form-group mb-4 text-left">
                        <label class="cw-label mb-2 d-block">KPI Attivi in Dashboard</label>
                        <div class="d-flex flex-wrap" style="gap: 15px;">
                          <div class="custom-control custom-checkbox mr-3">
                            <input type="checkbox" class="custom-control-input" id="kpi_buche" value="buche_riparate" v-model="commessaForm.active_kpis">
                            <label class="custom-control-label font-weight-semibold text-dark cursor-pointer" for="kpi_buche">Buche Riparate</label>
                          </div>
                          <div class="custom-control custom-checkbox mr-3">
                            <input type="checkbox" class="custom-control-input" id="kpi_sfalci" value="metri_sfalciati" v-model="commessaForm.active_kpis">
                            <label class="custom-control-label font-weight-semibold text-dark cursor-pointer" for="kpi_sfalci">Metri Sfalciati</label>
                          </div>
                          <div class="custom-control custom-checkbox mr-3">
                            <input type="checkbox" class="custom-control-input" id="kpi_alberi" value="alberi_potati" v-model="commessaForm.active_kpis">
                            <label class="custom-control-label font-weight-semibold text-dark cursor-pointer" for="kpi_alberi">Alberi Potati</label>
                          </div>
                          <div class="custom-control custom-checkbox mr-3">
                            <input type="checkbox" class="custom-control-input" id="kpi_irrigazione" value="interventi_irrigazione" v-model="commessaForm.active_kpis">
                            <label class="custom-control-label font-weight-semibold text-dark cursor-pointer" for="kpi_irrigazione">Interventi Irrigazione</label>
                          </div>
                          <div class="custom-control custom-checkbox mr-3">
                            <input type="checkbox" class="custom-control-input" id="kpi_igiene" value="interventi_igiene" v-model="commessaForm.active_kpis">
                            <label class="custom-control-label font-weight-semibold text-dark cursor-pointer" for="kpi_igiene">Interventi Igiene</label>
                          </div>
                          <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="kpi_luce" value="punti_luce_ripristinati" v-model="commessaForm.active_kpis">
                            <label class="custom-control-label font-weight-semibold text-dark cursor-pointer" for="kpi_luce">Punti Luce Ripristinati</label>
                          </div>
                        </div>
                      </div>

                      <div class="d-flex" style="gap:12px;">
                        <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border flex-grow-1" @click="cancelEditCommessa" v-if="commessaForm.id">
                          Annulla
                        </button>
                        <button type="submit" class="cw-btn-submit flex-grow-1" :disabled="submittingCommessa">
                          <span class="spinner-border spinner-border-sm mr-2" v-if="submittingCommessa"></span>
                          <i class="mdi mdi-check mr-1" v-else></i>
                          {{ commessaForm.id ? 'Salva Modifiche' : 'Aggiungi Commessa' }}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- CREATE MODULE MODE -->
          <div v-if="mode === 'create-module-commessa'" class="row justify-content-center">
            <div class="col-lg-10 col-md-10 mb-4">
              <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:12px;">
                <h4 class="mb-0 font-weight-bold text-dark">
                  Nuovo Modulo — <span class="text-primary">{{ selectedCommessa && selectedCommessa.nome }}</span>
                </h4>
                <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'edit-commessa-modules'">
                  <i class="mdi mdi-arrow-left mr-1"></i> Torna ai Moduli
                </button>
              </div>

              <div class="cw-card text-left">
                <div class="cw-section-title">
                  <span class="cw-step-num">1</span>{{ moduleForm.id ? 'Modifica Modulo' : 'Dettagli Modulo' }}
                </div>
                <form @submit.prevent="saveModule">
                  <div class="form-group mb-4 text-left">
                    <label class="cw-label">Nome Modulo <span class="text-danger">*</span></label>
                    <div class="cw-input-wrap">
                      <i class="mdi mdi-layers-outline cw-field-icon"></i>
                      <input
                        v-model="moduleForm.nome"
                        class="cw-input"
                        placeholder="Es. Illuminazione Pubblica"
                        required
                        @input="generateModuleCodice"
                      >
                    </div>
                  </div>

                  <div class="form-group mb-4 text-left">
                    <label class="cw-label">Codice (Autogenerato) <span class="text-danger">*</span></label>
                    <div class="cw-input-wrap">
                      <i class="mdi mdi-code-tags cw-field-icon"></i>
                      <input
                        v-model="moduleForm.codice"
                        class="cw-input"
                        style="background:#f8fafc;"
                        required
                        readonly
                      >
                    </div>
                  </div>

                  <div class="form-group mb-4 text-left">
                    <label class="cw-label">Icona <span class="text-danger">*</span></label>
                    <div class="cw-select-wrap">
                      <i class="mdi mdi-shape-outline cw-field-icon"></i>
                      <select v-model="moduleForm.icona" class="cw-select" required>
                        <option value="ti-layers">Default (ti-layers)</option>
                        <option value="ti-settings">Impostazioni (ti-settings)</option>
                        <option value="ti-bolt">Energia / Elettricità (ti-bolt)</option>
                        <option value="ti-help-alt">Supporto (ti-help-alt)</option>
                        <option value="ti-alert">Emergenza (ti-alert)</option>
                        <option value="ti-truck">Logistica / Veicoli (ti-truck)</option>
                        <option value="ti-paint-roller">Edilizia / Pittura (ti-paint-roller)</option>
                        <option value="ti-trash">Rifiuti / Igiene (ti-trash)</option>
                      </select>
                      <i class="mdi mdi-chevron-down cw-select-arrow"></i>
                    </div>
                  </div>

                  <div class="cw-footer d-flex align-items-center justify-content-between flex-wrap">
                    <small class="text-muted">
                      <i class="mdi mdi-asterisk text-danger" style="font-size:0.6rem;"></i> Campi obbligatori
                    </small>
                    <div class="d-flex" style="gap:12px;">
                      <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'edit-commessa-modules'">Annulla</button>
                      <button type="submit" class="cw-btn-submit" :disabled="submitting">
                        <span class="spinner-border spinner-border-sm mr-2" v-if="submitting"></span>
                        <i class="mdi mdi-check-circle-outline mr-1" v-else></i>
                        {{ moduleForm.id ? 'Salva Modifiche' : 'Crea Modulo' }}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Navbar } from "@/components";

export default {
  components: { Navbar },

  data() {
    return {
      tipologia: localStorage.getItem("tipologia"),
      mode: "list", // 'list', 'create', 'edit-modules'

      // List state
      companies: [],
      allModules: [],
      companyModulesMap: {},
      commessaModules: [],
      selectedCommessa: null,
      searchQuery: "",
      loadingCompanies: false,

      // Create form
      form: {
        nome: "",
        partita_iva: "",
        email: "",
        pec: "",
        indirizzo: "",
        citta: "",
        cap: "",
        provincia: "",
        telefono: "",
        nome_referente: "",
        cognome_referente: "",
        logo_url: "",
        selectedModules: [],
      },
      editingCompanyId: null,
      submitting: false,

      // Edit modules state
      selectedCompany: null,
      editModulesSelection: [],
      companyInterventions: [],
      companyCommesse: [],
      loadingModules: false,
      loadingInterventions: false,
      loadingCommesse: false,
      commessaForm: {
        id: null,
        nome: "",
        codice_commessa: "",
        cig: "",
        cup: "",
        data_inizio: "",
        data_fine: "",
        importo: "",
        stazione_appaltante: "",
        descrizione: "",
        active_kpis: ['buche_riparate', 'metri_sfalciati', 'interventi_igiene', 'punti_luce_ripristinati', 'alberi_potati', 'interventi_irrigazione']
      },
      submittingCommessa: false,

      // Quick add intervention state
      quickForm: {
        nome: "",
        codice: "",
        fk_module: ""
      },
      quickSubmitting: false,

      // Create module form
      moduleForm: {
        id: null,
        nome: "",
        codice: "",
        icona: "ti-layers"
      },

      // New asset and intervention config state
      selectedConfigModule: null,
      configModuleAssets: [],
      selectedConfigAssetType: null,
      loadingAssets: false,
      submittingAsset: false,
      submittingIntervento: false,
      newAssetForm: { nome: "", codice: "" },
      newInterventoForm: { nome: "", codice: "" },
      allInterventions: [],
      
      // Team types config state
      configModuleTeamTypes: [],
      loadingTeamTypes: false,
    };
  },

  computed: {
    filteredCompanies() {
      if (!this.searchQuery) return this.companies;
      const term = this.searchQuery.toLowerCase();
      return this.companies.filter(
        (c) =>
          (c.nome && c.nome.toLowerCase().includes(term)) ||
          (c.partita_iva && c.partita_iva.toLowerCase().includes(term)) ||
          (c.email && c.email.toLowerCase().includes(term))
      );
    },
    filteredInterventions() {
      if (!this.selectedConfigModule) return [];
      return this.allInterventions.filter(
        (i) => i.fk_module === this.selectedConfigModule.id
      );
    },
    stats() {
      return [
        {
          label: "Aziende Registrate",
          value: this.companies.length,
          icon: "ti-building",
          color: "linear-gradient(135deg, #667eea, #764ba2)",
        },
        {
          label: "Moduli Creati (Totale)",
          value: this.allModules.length,
          icon: "ti-layers-alt",
          color: "linear-gradient(135deg, #11998e, #38ef7d)",
        }
      ];
    },
  },

  mounted() {
    if (this.tipologia !== "D") {
      this.$router.push({ name: "home" });
      return;
    }
    this.loadAll();
  },

  methods: {
    async loadAll() {
      this.loadingCompanies = true;
      try {
        const [companiesRes, modulesRes] = await Promise.all([
          this.axios.get("/companies"),
          this.axios.get("/modules"),
        ]);
        this.companies = Array.isArray(companiesRes.data) ? companiesRes.data : [];
        this.allModules = Array.isArray(modulesRes.data) ? modulesRes.data : [];
      } catch (e) {
        console.error("Errore caricamento aziende:", e);
        this.$swal.fire("Errore", "Impossibile caricare le aziende.", "error");
      } finally {
        this.loadingCompanies = false;
      }
    },

    setCreateMode() {
      this.form = { 
        nome: "", partita_iva: "", email: "", pec: "", indirizzo: "", 
        citta: "", cap: "", provincia: "", telefono: "", 
        nome_referente: "", cognome_referente: "", logo_url: "", selectedModules: [] 
      };
      this.mode = "create";
    },

    toggleModule(id) {
      const idx = this.form.selectedModules.indexOf(id);
      if (idx === -1) this.form.selectedModules.push(id);
      else this.form.selectedModules.splice(idx, 1);
    },

    editCompany(company) {
      this.editingCompanyId = company.id;
      this.form = {
        nome: company.nome || "",
        partita_iva: company.partita_iva || "",
        email: company.email || "",
        pec: company.pec || "",
        indirizzo: company.indirizzo || "",
        citta: company.citta || "",
        cap: company.cap || "",
        provincia: company.provincia || "",
        telefono: company.telefono || "",
        nome_referente: company.nome_referente || "",
        cognome_referente: company.cognome_referente || "",
        logo_url: company.logo_url || "",
        selectedModules: []
      };
      this.mode = "edit";
    },
    
    async deleteCompany(id) {
      const result = await this.$swal.fire({
        title: "Sei sicuro?",
        text: "L'eliminazione dell'azienda cancellerà tutte le commesse e i moduli ad essa collegati. L'azione è irreversibile.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sì, elimina",
        cancelButtonText: "Annulla"
      });
      
      if (result.isConfirmed) {
        try {
          const res = await this.axios.delete(`/companies/${id}`);
          if (res.data && res.data.success) {
            this.$swal.fire("Eliminata!", "L'azienda è stata eliminata.", "success");
            await this.loadAll();
          } else {
            this.$swal.fire("Errore", res.data.error || "Impossibile eliminare l'azienda", "error");
          }
        } catch (e) {
          console.error("Errore durante l'eliminazione", e);
          this.$swal.fire("Errore", "Si è verificato un errore di rete.", "error");
        }
      }
    },

    async saveCompany() {
      if (!this.form.nome.trim()) {
        this.$swal.fire("Attenzione", "Il nome è obbligatorio.", "warning");
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          nome: this.form.nome.trim(),
          partita_iva: this.form.partita_iva.trim(),
          email: this.form.email.trim(),
          pec: this.form.pec.trim(),
          indirizzo: this.form.indirizzo.trim(),
          citta: this.form.citta.trim(),
          cap: this.form.cap.trim(),
          provincia: this.form.provincia.trim(),
          telefono: this.form.telefono.trim(),
          nome_referente: this.form.nome_referente.trim(),
          cognome_referente: this.form.cognome_referente.trim(),
          logo_url: this.form.logo_url.trim()
        };
        
        let res;
        if (this.mode === 'edit' && this.editingCompanyId) {
          res = await this.axios.put(`/companies/${this.editingCompanyId}`, payload);
        } else {
          res = await this.axios.post("/companies", payload);
        }
        
        if (res.data && res.data.success) {
          this.$swal.fire(this.mode === 'edit' ? "Salvata!" : "Creata!", `L'azienda "${this.form.nome}" è stata salvata con successo.`, "success");
          this.mode = "list";
          this.editingCompanyId = null;
          await this.loadAll();
        } else {
          this.$swal.fire("Errore", res.data.error || "Impossibile salvare l'azienda.", "error");
        }
      } catch (e) {
        console.error("Errore salvataggio azienda:", e);
        this.$swal.fire("Errore", "Impossibile contattare il server.", "error");
      } finally {
        this.submitting = false;
      }
    },

    async openEditModules(company) {
      this.selectedCompany = company;
      this.editModulesSelection = (this.companyModulesMap[company.id] || []).map((m) => m.id);
      this.loadingModules = false;
      this.mode = "edit-modules";
      await this.loadCompanyInterventions(company.id);
    },

    toggleEditModule(id) {
      const idx = this.editModulesSelection.indexOf(id);
      if (idx === -1) this.editModulesSelection.push(id);
      else this.editModulesSelection.splice(idx, 1);
    },

    async loadCompanyInterventions(companyId) {
      this.loadingInterventions = true;
      try {
        const res = await this.axios.get(`/companies/${companyId}/interventions`);
        this.companyInterventions = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Errore caricamento interventi:", e);
        this.companyInterventions = [];
      } finally {
        this.loadingInterventions = false;
      }
    },

    async openEditCommesse(company) {
      this.selectedCompany = company;
      this.mode = "edit-commesse";
      this.commessaForm = {
        id: null, nome: "", codice_commessa: "", cig: "", cup: "", data_inizio: "", data_fine: "", importo: "", stazione_appaltante: "", descrizione: "",
        active_kpis: ['buche_riparate', 'metri_sfalciati', 'interventi_igiene', 'punti_luce_ripristinati', 'alberi_potati', 'interventi_irrigazione']
      };
      await this.loadCompanyCommesse(company.id);
    },

    editCommessa(comm) {
      this.commessaForm = {
        id: comm.id,
        nome: comm.nome || "",
        codice_commessa: comm.codice_commessa || "",
        cig: comm.cig || "",
        cup: comm.cup || "",
        data_inizio: comm.data_inizio || "",
        data_fine: comm.data_fine || "",
        importo: comm.importo || "",
        stazione_appaltante: comm.stazione_appaltante || "",
        descrizione: comm.descrizione || "",
        active_kpis: Array.isArray(comm.active_kpis) ? [...comm.active_kpis] : ['buche_riparate', 'metri_sfalciati', 'interventi_igiene', 'punti_luce_ripristinati', 'alberi_potati', 'interventi_irrigazione']
      };
    },
    
    cancelEditCommessa() {
      this.commessaForm = {
        id: null, nome: "", codice_commessa: "", cig: "", cup: "", data_inizio: "", data_fine: "", importo: "", stazione_appaltante: "", descrizione: "",
        active_kpis: ['buche_riparate', 'metri_sfalciati', 'interventi_igiene', 'punti_luce_ripristinati', 'alberi_potati', 'interventi_irrigazione']
      };
    },

    
    async openCommessaModules(commessa) {
      this.selectedCommessa = commessa;
      this.mode = "edit-commessa-modules";
      this.selectedConfigModule = null;
      this.configModuleAssets = [];
      this.selectedConfigAssetType = null;
      this.newAssetForm = { nome: "", codice: "" };
      this.newInterventoForm = { nome: "", codice: "" };
      await this.loadCommessaModules(commessa.id);
      await this.loadAllInterventions(commessa.id);
    },

    async loadCommessaModules(commessaId) {
      this.loadingModules = true;
      try {
        const res = await this.axios.get(`/commesse/${commessaId}/modules`);
        this.commessaModules = Array.isArray(res.data) ? res.data : [];
        if (this.commessaModules.length > 0) {
          await this.selectConfigModule(this.commessaModules[0]);
        } else {
          this.selectedConfigModule = null;
          this.configModuleAssets = [];
          this.selectedConfigAssetType = null;
        }
      } catch (e) {
        console.error("Errore caricamento moduli commessa:", e);
        this.commessaModules = [];
      } finally {
        this.loadingModules = false;
      }
    },

    async loadCompanyCommesse(companyId) {
      this.loadingCommesse = true;
      try {
        const res = await this.axios.get(`/companies/${companyId}/commesse`);
        this.companyCommesse = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Errore caricamento commesse:", e);
        this.companyCommesse = [];
      } finally {
        this.loadingCommesse = false;
      }
    },

    async saveCommessa() {
      if (!this.commessaForm.nome.trim()) {
        this.$swal.fire("Attenzione", "Inserire il nome della commessa.", "warning");
        return;
      }
      this.submittingCommessa = true;
      try {
        let res;
        if (this.commessaForm.id) {
          res = await this.axios.put(`/companies/${this.selectedCompany.id}/commesse/${this.commessaForm.id}`, this.commessaForm);
        } else {
          res = await this.axios.post(`/companies/${this.selectedCompany.id}/commesse`, this.commessaForm);
        }
        
        if (res.data && res.data.success) {
          this.$swal.fire(this.commessaForm.id ? "Modificata" : "Aggiunta", "Operazione completata con successo.", "success");
          this.commessaForm = {
            id: null, nome: "", codice_commessa: "", cig: "", cup: "", data_inizio: "", data_fine: "", importo: "", stazione_appaltante: "", descrizione: "",
            active_kpis: ['buche_riparate', 'metri_sfalciati', 'interventi_igiene', 'punti_luce_ripristinati', 'alberi_potati', 'interventi_irrigazione']
          };
          await this.loadCompanyCommesse(this.selectedCompany.id);
        } else {
          this.$swal.fire("Errore", res.data.error || "Impossibile salvare la commessa", "error");
        }
      } catch (e) {
        console.error("Errore salvataggio commessa", e);
      } finally {
        this.submittingCommessa = false;
      }
    },

    async deleteCommessa(commessaId) {
      const confirm = await this.$swal.fire({
        title: 'Sei sicuro?',
        text: "Vuoi eliminare questa commessa?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sì, elimina!'
      });

      if (confirm.isConfirmed) {
        try {
          const res = await this.axios.delete(`/companies/${this.selectedCompany.id}/commesse/${commessaId}`);
          if (res.data && res.data.success) {
            this.$swal.fire("Eliminata", "La commessa è stata eliminata.", "success");
            await this.loadCompanyCommesse(this.selectedCompany.id);
          } else {
            this.$swal.fire("Errore", res.data.error || "Errore durante l'eliminazione", "error");
          }
        } catch(e) {
          console.error("Errore eliminazione", e);
        }
      }
    },

    async saveModules() {
      if (!this.selectedCompany) return;
      this.submitting = true;
      try {
        const res = await this.axios.post(`/companies/${this.selectedCompany.id}/modules`, {
          module_ids: this.editModulesSelection,
        });
        if (res.data && res.data.success) {
          this.$swal.fire(
            "Salvato!",
            `Configurazione moduli per "${this.selectedCompany.nome}" aggiornata. Le tipologie di intervento sono state sincronizzate automaticamente.`,
            "success"
          );
          this.mode = "edit-commessa-modules";
          await this.loadCommessaModules(this.selectedCommessa.id);
        } else {
          this.$swal.fire("Errore", res.data.error || "Impossibile salvare la configurazione.", "error");
        }
      } catch (e) {
        console.error("Errore salvataggio moduli:", e);
        this.$swal.fire("Errore", "Impossibile contattare il server.", "error");
      } finally {
        this.submitting = false;
      }
    },

    getModuleName(id) {
      const mod = this.commessaModules.find(m => m.id === id) || this.allModules.find(m => m.id === id);
      return mod ? mod.nome : '—';
    },

    generateQuickCodice() {
      if (!this.quickForm.nome) { this.quickForm.codice = ""; return; }
      this.quickForm.codice = this.quickForm.nome
        .toLowerCase().trim()
        .replace(/[\s-]+/g, "_")
        .replace(/[^\w]/g, "");
    },

    async quickAddIntervento() {
      if (!this.quickForm.nome || !this.quickForm.codice || !this.quickForm.fk_module) {
        this.$swal.fire("Attenzione", "Tutti i campi sono obbligatori.", "warning");
        return;
      }
      this.quickSubmitting = true;
      try {
        // 1. Aggiungi globalmente al catalogo del modulo
        const formData = new FormData();
        formData.append("nome", this.quickForm.nome);
        formData.append("codice", this.quickForm.codice);
        formData.append("fk_module", this.quickForm.fk_module);
        
        const resGlobal = await this.axios.post('/tipologie-intervento', formData);
        if (resGlobal.data && resGlobal.data.success) {
          // 2. Abilita per questa azienda
          const resCompany = await this.axios.post(`/companies/${this.selectedCompany.id}/interventions`, {
            nome: this.quickForm.nome,
            codice: this.quickForm.codice,
            fk_module: this.quickForm.fk_module
          });
          
          if (resCompany.data && resCompany.data.success) {
            this.$swal.fire("Aggiunta!", "Tipologia di intervento creata e abilitata per questa azienda.", "success");
            // Reset form
            this.quickForm = { nome: "", codice: "", fk_module: "" };
            // Ricarica la lista per questa azienda
            await this.loadCompanyInterventions(this.selectedCompany.id);
          } else {
            this.$swal.fire("Errore", resCompany.data.error || "Impossibile abilitare l'intervento per l'azienda.", "error");
          }
        } else {
          this.$swal.fire("Errore", resGlobal.data.error || "Impossibile creare l'intervento nel catalogo.", "error");
        }
      } catch (e) {
        console.error("Errore aggiunta rapida:", e);
        this.$swal.fire("Errore", "Impossibile contattare il server.", "error");
      } finally {
        this.quickSubmitting = false;
      }
    },

    generateModuleCodice() {
      if (!this.moduleForm.nome) { this.moduleForm.codice = ""; return; }
      this.moduleForm.codice = this.moduleForm.nome
        .toLowerCase().trim()
        .replace(/[\s-]+/g, "_")
        .replace(/[^\w]/g, "");
    },

    setCreateModuleMode() {
      this.moduleForm = { id: null, nome: "", codice: "", icona: "ti-layers" };
      this.mode = "create-module-commessa";
    },

    editModule(mod) {
      this.moduleForm = {
        id: mod.id,
        nome: mod.nome,
        codice: mod.codice,
        icona: mod.icona || "ti-layers"
      };
      this.mode = "create-module-commessa";
    },

    async deleteModule(modId) {
      const confirm = await this.$swal.fire({
        title: 'Sei sicuro?',
        text: "Vuoi eliminare questo modulo? Verranno eliminate anche le tipologie di intervento collegate.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sì, elimina!'
      });

      if (confirm.isConfirmed) {
        try {
          const res = await this.axios.delete(`/commesse/${this.selectedCommessa.id}/modules/${modId}`);
          if (res.data && res.data.success) {
            this.$swal.fire("Eliminato", "Il modulo è stato eliminato.", "success");
            await this.loadCommessaModules(this.selectedCommessa.id);
          } else {
            this.$swal.fire("Errore", res.data.error || "Errore durante l'eliminazione", "error");
          }
        } catch(e) {
          console.error("Errore eliminazione modulo", e);
        }
      }
    },

    async saveModule() {
      if (!this.moduleForm.nome || !this.moduleForm.codice) {
        this.$swal.fire("Attenzione", "Nome e Codice sono obbligatori.", "warning");
        return;
      }
      this.submitting = true;
      try {
        let res;
        if (this.moduleForm.id) {
          res = await this.axios.put(`/commesse/${this.selectedCommessa.id}/modules/${this.moduleForm.id}`, this.moduleForm);
        } else {
          res = await this.axios.post(`/commesse/${this.selectedCommessa.id}/modules`, this.moduleForm);
        }
        
        if (res.data && res.data.success) {
          this.$swal.fire(this.moduleForm.id ? "Salvato" : "Creato", "Il modulo è stato salvato con successo.", "success");
          this.mode = "edit-commessa-modules";
          await this.loadCommessaModules(this.selectedCommessa.id);
        } else {
          this.$swal.fire("Errore", res.data.error || "Impossibile salvare il modulo", "error");
        }
      } catch (e) {
        console.error("Errore salvataggio modulo", e);
      } finally {
        this.submitting = false;
      }
    },

    async loadAllInterventions(commessaId) {
      try {
        const res = await this.axios.get(`/tipologie-intervento?commessa_id=${commessaId}`);
        this.allInterventions = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Errore caricamento tipologie intervento:", e);
        this.allInterventions = [];
      }
    },

    async selectConfigModule(mod) {
      this.selectedConfigModule = mod;
      this.selectedConfigAssetType = null;
      await this.loadConfigModuleAssets();
      await this.loadModuleTeamTypes();
    },

    async loadModuleTeamTypes() {
      if (!this.selectedConfigModule || !this.selectedCommessa || !this.selectedCompany) return;
      this.loadingTeamTypes = true;
      try {
        const res = await this.axios.get(`/companies/${this.selectedCompany.id}/tipologie-squadra`);
        if (res.data && Array.isArray(res.data)) {
          this.configModuleTeamTypes = res.data.filter(
            t => t.fk_commessa === this.selectedCommessa.id && t.fk_module === this.selectedConfigModule.id
          );
        } else {
          this.configModuleTeamTypes = [];
        }
      } catch (e) {
        console.error("Errore caricamento tipologie squadra:", e);
        this.configModuleTeamTypes = [];
      } finally {
        this.loadingTeamTypes = false;
      }
    },

    async loadConfigModuleAssets() {
      if (!this.selectedConfigModule || !this.selectedCommessa) return;
      this.loadingAssets = true;
      try {
        const res = await this.axios.get(`/modules/${this.selectedConfigModule.id}/asset_types?commessa_id=${this.selectedCommessa.id}`);
        this.configModuleAssets = (res.data && res.data.data) ? res.data.data : [];
        if (this.configModuleAssets.length > 0) {
          if (!this.selectedConfigAssetType || !this.configModuleAssets.some(a => a.id === this.selectedConfigAssetType.id)) {
            this.selectConfigAssetType(this.configModuleAssets[0]);
          } else {
            const updatedAsset = this.configModuleAssets.find(a => a.id === this.selectedConfigAssetType.id);
            this.selectConfigAssetType(updatedAsset);
          }
        } else {
          this.selectedConfigAssetType = null;
        }
      } catch (e) {
        console.error("Errore caricamento asset modulo:", e);
        this.configModuleAssets = [];
        this.selectedConfigAssetType = null;
      } finally {
        this.loadingAssets = false;
      }
    },

    generateNewAssetCodice() {
      if (!this.newAssetForm.nome) { this.newAssetForm.codice = ""; return; }
      this.newAssetForm.codice = this.newAssetForm.nome
        .toLowerCase().trim()
        .replace(/[\s-]+/g, "_")
        .replace(/[^\w]/g, "");
    },

    async addConfigAsset() {
      if (!this.newAssetForm.nome || !this.newAssetForm.codice || !this.selectedConfigModule) {
        this.$swal.fire("Attenzione", "Il nome dell'asset è obbligatorio.", "warning");
        return;
      }
      this.submittingAsset = true;
      try {
        const res = await this.axios.post('/asset_types', {
          fk_module: this.selectedConfigModule.id,
          nome: this.newAssetForm.nome,
          codice: this.newAssetForm.codice,
          campi_schema: [],
          fk_commessa: this.selectedCommessa.id
        });
        if (res.data && res.data.success) {
          this.$swal.fire("Aggiunto!", "Asset aggiunto con successo.", "success");
          this.newAssetForm = { nome: "", codice: "" };
          await this.loadConfigModuleAssets();
        } else {
          this.$swal.fire("Errore", res.data.error || "Impossibile salvare l'asset.", "error");
        }
      } catch (e) {
        console.error("Errore salvataggio asset:", e);
        this.$swal.fire("Errore", "Impossibile contattare il server.", "error");
      } finally {
        this.submittingAsset = false;
      }
    },

    async deleteConfigAsset(assetTypeId) {
      const confirm = await this.$swal.fire({
        title: 'Sei sicuro?',
        text: "Vuoi eliminare questo asset? Verranno eliminate anche le associazioni e i dati collegati.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sì, elimina!'
      });

      if (confirm.isConfirmed) {
        try {
          const res = await this.axios.delete(`/asset_types/${assetTypeId}`);
          if (res.data && res.data.success) {
            this.$swal.fire("Eliminato", "L'asset è stato rimosso.", "success");
            await this.loadConfigModuleAssets();
          } else {
            this.$swal.fire("Errore", res.data.error || "Errore durante l'eliminazione", "error");
          }
        } catch(e) {
          console.error("Errore eliminazione asset", e);
        }
      }
    },

    selectConfigAssetType(assetType) {
      this.selectedConfigAssetType = assetType;
      this.newInterventoForm = { nome: "", codice: "" };
    },

    async toggleInterventoAssociation(interventoId) {
      if (!this.selectedConfigAssetType) return;
      
      const idx = this.selectedConfigAssetType.interventi_ids.indexOf(interventoId);
      if (idx === -1) {
        this.selectedConfigAssetType.interventi_ids.push(interventoId);
      } else {
        this.selectedConfigAssetType.interventi_ids.splice(idx, 1);
      }

      try {
        const res = await this.axios.post(`/asset_types/${this.selectedConfigAssetType.id}/interventi`, {
          interventi_ids: this.selectedConfigAssetType.interventi_ids
        });
        if (!res.data || !res.data.success) {
          this.$swal.fire("Errore", "Impossibile salvare l'associazione tipologia intervento.", "error");
          if (idx === -1) {
            this.selectedConfigAssetType.interventi_ids.pop();
          } else {
            this.selectedConfigAssetType.interventi_ids.push(interventoId);
          }
        }
      } catch (e) {
        console.error("Errore salvataggio associazione:", e);
        this.$swal.fire("Errore", "Impossibile contattare il server.", "error");
        if (idx === -1) {
          this.selectedConfigAssetType.interventi_ids.pop();
        } else {
          this.selectedConfigAssetType.interventi_ids.push(interventoId);
        }
      }
    },

    generateNewInterventoCodice() {
      if (!this.newInterventoForm.nome) { this.newInterventoForm.codice = ""; return; }
      this.newInterventoForm.codice = this.newInterventoForm.nome
        .toLowerCase().trim()
        .replace(/[\s-]+/g, "_")
        .replace(/[^\w]/g, "");
    },

    async createNewInterventoForAsset() {
      if (!this.newInterventoForm.nome || !this.newInterventoForm.codice || !this.selectedConfigAssetType || !this.selectedConfigModule) {
        this.$swal.fire("Attenzione", "Il nome dell'intervento è obbligatorio.", "warning");
        return;
      }
      this.submittingIntervento = true;
      try {
        const res = await this.axios.post('/tipologie-intervento', {
          nome: this.newInterventoForm.nome,
          codice: this.newInterventoForm.codice,
          fk_module: this.selectedConfigModule.id,
          fk_commessa: this.selectedCommessa.id
        });
        if (res.data && res.data.success) {
          const newInterventoId = res.data.id;
          
          this.allInterventions.push({
            id: newInterventoId,
            codice: this.newInterventoForm.codice,
            nome: this.newInterventoForm.nome,
            fk_module: this.selectedConfigModule.id,
            fk_commessa: this.selectedCommessa.id
          });
          
          this.selectedConfigAssetType.interventi_ids.push(newInterventoId);
          await this.axios.post(`/asset_types/${this.selectedConfigAssetType.id}/interventi`, {
            interventi_ids: this.selectedConfigAssetType.interventi_ids
          });
          
          this.$swal.fire("Creato e Associato!", "Tipologia intervento creata e associata a questo asset.", "success");
          this.newInterventoForm = { nome: "", codice: "" };
        } else {
          this.$swal.fire("Errore", res.data.error || "Impossibile creare l'intervento.", "error");
        }
      } catch (e) {
        console.error("Errore salvataggio intervento:", e);
        this.$swal.fire("Errore", "Impossibile contattare il server.", "error");
      } finally {
        this.submittingIntervento = false;
      }
    },
  },
};
</script>

<style scoped>
.font-weight-semibold { font-weight: 500; }
.font-weight-bold { font-weight: 700; }

/* ===== Design System ===== */
.cw-header-icon {
  width: 48px; height: 48px; border-radius: 14px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(247,147,30,0.35);
  flex-shrink: 0;
}
.cw-header-icon .mdi { color: #fff; font-size: 1.5rem; }

.cw-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  padding: 28px;
  border: 1px solid rgba(0,0,0,0.05);
}

.cw-section-title {
  font-size: 0.82rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748b; margin-bottom: 22px;
  display: flex; align-items: center;
}
.cw-step-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.7rem; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
  margin-right: 10px; flex-shrink: 0;
}

.cw-label {
  font-size: 0.75rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em;
  color: #94a3b8; margin-bottom: 8px; display: block;
}

.cw-input-wrap { position: relative; display: flex; align-items: center; }
.cw-field-icon {
  position: absolute; left: 12px;
  color: #94a3b8; font-size: 1rem; z-index: 2; pointer-events: none;
}
.cw-input {
  width: 100%; height: 46px; padding: 0 14px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; font-size: 0.92rem; font-weight: 500; color: #1e293b;
  transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-input:focus {
  border-color: #F7931E;
  box-shadow: 0 0 0 3px rgba(247,147,30,0.12);
  background: #fff;
}

.cw-select-wrap { position: relative; display: flex; align-items: center; }
.cw-select {
  width: 100%; height: 46px; padding: 0 38px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; appearance: none; -webkit-appearance: none;
  font-size: 0.92rem; font-weight: 500; color: #1e293b;
  cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-select:focus {
  border-color: #F7931E;
  box-shadow: 0 0 0 3px rgba(247,147,30,0.12);
  background: #fff;
}
.cw-select-arrow {
  position: absolute; right: 14px;
  color: #94a3b8; font-size: 1rem; pointer-events: none;
}

.cw-btn-submit {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 10px 28px; border: none; border-radius: 30px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.9rem; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 14px rgba(247,147,30,0.35);
  transition: all 0.2s;
}
.cw-btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #E07B00, #C96A00);
  box-shadow: 0 6px 18px rgba(247,147,30,0.45);
  transform: translateY(-1px);
}
.cw-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
.cw-btn-submit.w-100 { width: 100%; }

.cw-footer {
  background: #fff; border-radius: 14px;
  padding: 18px 24px; border: 1px solid rgba(0,0,0,0.05);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ===== Tabella Premium ===== */
.au-table { width: 100%; border-collapse: collapse; }
.au-table thead tr { border-bottom: 2px solid #f1f5f9; }
.au-table thead th {
  padding: 10px 16px; font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em; color: #94a3b8;
  background: #f8fafc;
}
.au-table tbody tr { border-bottom: 1px solid #f1f5f9; transition: background 0.15s; }
.au-table tbody tr:hover { background: #f8fafc; }
.au-table td { padding: 14px 16px; vertical-align: middle; }

/* ===== Badges ===== */
.au-company-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 0.78rem; font-weight: 600;
  background: #f1f5f9; color: #64748b;
}

/* ===== Bottone Danger ===== */
.au-btn-danger {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 6px 12px; border-radius: 20px;
  border: 1.5px solid #fecaca; background: #fff5f5;
  color: #991b1b; font-size: 0.82rem; font-weight: 700;
  cursor: pointer; transition: all 0.15s;
}
.au-btn-danger:hover { background: #fee2e2; border-color: #ef4444; }

/* ===== Search bar ===== */
.au-search-wrap {
  position: relative; display: flex; align-items: center;
  background: #f8fafc; border: 1.5px solid #e2e8f0;
  border-radius: 30px; height: 44px; padding: 0 16px 0 38px;
  min-width: 260px; transition: border-color 0.2s;
}
.au-search-wrap:focus-within { border-color: #F7931E; background: #fff; }
.au-search-icon {
  position: absolute; left: 14px; color: #94a3b8; font-size: 1rem; pointer-events: none;
}
.au-search-input {
  border: none; background: transparent; outline: none;
  font-size: 0.9rem; color: #1e293b; width: 100%;
}

/* ===== Stat cards ===== */
.stat-icon {
  width: 44px; height: 44px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.stat-icon i { font-size: 1.2rem; color: #fff; }
.stat-number { font-size: 1.4rem; line-height: 1; color: #1e293b; font-weight: 700; }
.stat-label { font-size: 0.75rem; }

/* ===== Company avatar ===== */
.company-avatar {
  width: 34px; height: 34px; border-radius: 10px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-weight: 700; font-size: 0.9rem;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

/* ===== Module cards grid ===== */
.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}
.module-card {
  position: relative;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 16px 12px;
  border: 1.5px solid #e2e8f0; border-radius: 12px;
  cursor: pointer; transition: all 0.2s;
  background: #f8fafc; text-align: center; user-select: none;
}
.module-card:hover {
  border-color: #F5B871;
  box-shadow: 0 4px 12px rgba(247,147,30,0.12);
  transform: translateY(-2px);
  background: #FEF1E0;
}
.module-card--active {
  border-color: #F7931E;
  background: linear-gradient(135deg, #FEF1E0, #FEF1E0);
  box-shadow: 0 4px 12px rgba(247,147,30,0.18);
}
.module-card__icon {
  font-size: 1.6rem; color: #94a3b8;
  margin-bottom: 8px; transition: color 0.2s;
}
.module-card--active .module-card__icon { color: #F7931E; }
.module-card__name {
  font-size: 0.8rem; font-weight: 600; color: #64748b;
}
.module-card--active .module-card__name { color: #E07B00; }
.module-card__check {
  position: absolute; top: 6px; right: 6px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #e2e8f0;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.6rem; color: transparent; transition: all 0.2s;
}
.module-card--active .module-card__check {
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff;
}

/* ===== Company row ===== */
.company-row { transition: background 0.15s; }
</style>
